import React, { useState, useEffect, useRef } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ActorRunsTable } from "./ActorRunsTable";
import { LogViewer } from "@/pages/ScraperStatus";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowLeft, Download, Info, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Doc, Id } from "../../../convex/_generated/dataModel";
import { cn } from "@/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface GroupTabsProps {
  selectedGroup: Doc<"sourceGroups">;
  selectedRunId?: string | null;
  onRunSelect?: (runId: string | null) => void;
}

export function GroupTabs({
  selectedGroup,
  selectedRunId,
  onRunSelect,
}: GroupTabsProps) {
  const { toast } = useToast();
  const getRunDataset = useAction(api.apify.getRunDataset);
  const updateGroup = useMutation(api.sources.updateGroup);

  const [telegramChannelId, setTelegramChannelId] = useState<string>("");
  const [telegramMessageTemplate, setTelegramMessageTemplate] =
    useState<string>("");
  const [isSavingTelegram, setIsSavingTelegram] = useState(false);
  const [isTemplateVarsOpen, setIsTemplateVarsOpen] = useState(false);

  // Get runs for this group from database
  const databaseRuns = useQuery(
    api.runs.listByGroupId,
    selectedGroup?._id ? { groupId: selectedGroup._id } : "skip"
  ) as Doc<"runs">[] | undefined;

  // Get logs for selected run, or for the group if no run is selected
  const runLogs = useQuery(
    api.logs.listByRunId,
    selectedRunId ? { runId: selectedRunId } : "skip"
  ) as any[] | undefined;

  // Get logs for the group (using group-specific sourceId) when no run is selected
  const groupLogs = useQuery(
    api.logs.listBySource,
    selectedGroup?._id && !selectedRunId
      ? { sourceId: `group:${selectedGroup._id}` }
      : "skip"
  ) as any[] | undefined;

  const [datasetItems, setDatasetItems] = useState<any[]>([]);
  const [isLoadingDataset, setIsLoadingDataset] = useState(false);
  const datasetPreviewRef = useRef<HTMLDivElement>(null);

  // Initialize Telegram channel and template from group
  useEffect(() => {
    if (selectedGroup?.telegramChannelId) {
      setTelegramChannelId(selectedGroup.telegramChannelId);
    } else {
      setTelegramChannelId("");
    }
    if (selectedGroup?.telegramMessageTemplate) {
      setTelegramMessageTemplate(selectedGroup.telegramMessageTemplate);
    } else {
      // Default template
      setTelegramMessageTemplate(`💼 *{{title}}*
🏢 {{company}}
💰 {{type}}: {{rate}}
📍 {{location}}
{{#if url}}🔗 [View Job]({{url}}){{/if}}`);
    }
  }, [
    selectedGroup?.telegramChannelId,
    selectedGroup?.telegramMessageTemplate,
  ]);

  // Load dataset when a run is selected
  useEffect(() => {
    if (selectedRunId) {
      const loadDataset = async () => {
        setIsLoadingDataset(true);
        try {
          const result = await getRunDataset({ runId: selectedRunId });
          setDatasetItems(result?.items || []);
          if (result?.error) {
            toast({
              title: "Warning",
              description: result.error,
              variant: "destructive",
            });
          }
        } catch (error: any) {
          toast({
            title: "Failed to load dataset",
            description: error.message,
            variant: "destructive",
          });
        } finally {
          setIsLoadingDataset(false);
        }
      };
      loadDataset();
    } else {
      setDatasetItems([]);
    }
  }, [selectedRunId, getRunDataset, toast]);

  const handleRunClick = (runId: string) => {
    if (onRunSelect) {
      onRunSelect(runId);
      // Dataset will be loaded automatically via useEffect
    }
  };

  const handleBackToRuns = () => {
    if (onRunSelect) {
      onRunSelect(null);
    }
    setDatasetItems([]);
  };

  const downloadAsJSON = () => {
    if (datasetItems.length === 0) {
      toast({
        title: "No data to export",
        description: "The dataset is empty",
        variant: "destructive",
      });
      return;
    }

    const dataStr = JSON.stringify(datasetItems, null, 2);
    const dataBlob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `dataset-${selectedRunId?.slice(0, 12) || "export"}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: "Export started",
      description: "Dataset exported as JSON",
    });
  };

  const downloadAsCSV = () => {
    if (datasetItems.length === 0) {
      toast({
        title: "No data to export",
        description: "The dataset is empty",
        variant: "destructive",
      });
      return;
    }

    // Flatten nested objects for CSV
    const flattenObject = (obj: any, prefix = ""): Record<string, string> => {
      const flattened: Record<string, string> = {};
      for (const key in obj) {
        const value = obj[key];
        const newKey = prefix ? `${prefix}.${key}` : key;
        if (value === null || value === undefined) {
          flattened[newKey] = "";
        } else if (typeof value === "object" && !Array.isArray(value)) {
          Object.assign(flattened, flattenObject(value, newKey));
        } else if (Array.isArray(value)) {
          flattened[newKey] = JSON.stringify(value);
        } else {
          flattened[newKey] = String(value);
        }
      }
      return flattened;
    };

    // Get all unique keys from all items
    const allKeys = new Set<string>();
    datasetItems.forEach((item) => {
      const flattened = flattenObject(item);
      Object.keys(flattened).forEach((key) => allKeys.add(key));
    });

    const headers = Array.from(allKeys).sort();

    // Create CSV rows
    const csvRows = [
      headers.map((h) => `"${h.replace(/"/g, '""')}"`).join(","),
    ];

    datasetItems.forEach((item) => {
      const flattened = flattenObject(item);
      const row = headers.map((header) => {
        const value = flattened[header] || "";
        // Escape quotes and wrap in quotes
        return `"${String(value).replace(/"/g, '""')}"`;
      });
      csvRows.push(row.join(","));
    });

    const csvContent = csvRows.join("\n");
    const dataBlob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `dataset-${selectedRunId?.slice(0, 12) || "export"}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: "Export started",
      description: "Dataset exported as CSV",
    });
  };

  const handleSaveTelegramChannel = async () => {
    if (!selectedGroup) return;

    setIsSavingTelegram(true);
    try {
      await updateGroup({
        id: selectedGroup._id,
        telegramChannelId: telegramChannelId.trim() || undefined,
        telegramMessageTemplate: telegramMessageTemplate.trim() || undefined,
      });

      toast({
        title: "Telegram configuration saved",
        description: telegramChannelId.trim()
          ? `Jobs will be sent to ${telegramChannelId.trim()} after successful runs`
          : "Telegram notifications disabled for this group",
      });
    } catch (error: any) {
      console.error(
        "[GroupTabs] Failed to save Telegram configuration:",
        error
      );
      toast({
        title: "Failed to save Telegram configuration",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSavingTelegram(false);
    }
  };

  return (
    <Tabs defaultValue="logs" className="flex-1 flex flex-col min-h-0">
      <div className="px-6 pt-4 border-b border-border shrink-0">
        <TabsList className="bg-transparent h-9 p-0 space-x-6">
          <TabsTrigger
            value="logs"
            className="bg-transparent p-0 pb-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none text-muted-foreground data-[state=active]:text-foreground"
          >
            Live Logs
          </TabsTrigger>
          <TabsTrigger
            value="telegram"
            className="bg-transparent p-0 pb-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none text-muted-foreground data-[state=active]:text-foreground"
          >
            Notifications
          </TabsTrigger>
          <TabsTrigger
            value="runs"
            className="bg-transparent p-0 pb-2 rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none text-muted-foreground data-[state=active]:text-foreground"
          >
            Runs & Datasets
          </TabsTrigger>
        </TabsList>
      </div>

      <TabsContent
        value="logs"
        className="flex-1 p-4 min-h-0 mt-0 flex flex-col"
      >
        {selectedRunId && (
          <div className="mb-2 px-3 py-2 bg-primary/10 border border-primary/20 rounded-md text-xs">
            <span className="text-muted-foreground">
              Viewing logs for run:{" "}
            </span>
            <code className="font-mono text-primary">
              {selectedRunId.slice(0, 12)}...
            </code>
          </div>
        )}
        <div className="flex-1 min-h-0">
          <LogViewer
            logs={
              selectedRunId && runLogs ? runLogs : groupLogs ? groupLogs : []
            }
          />
        </div>
      </TabsContent>

      <TabsContent
        value="telegram"
        className="flex-1 p-8 min-h-0 mt-0 overflow-y-auto"
      >
        <div className="max-w-2xl mx-auto space-y-6">
          <div>
            <h3 className="text-sm font-semibold mb-1">
              Telegram Notifications
            </h3>
            <p className="text-xs text-muted-foreground">
              Configure a Telegram channel to receive filtered jobs from all
              sources in this group after successful runs. Only jobs that pass
              all filters will be sent.
            </p>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="group_telegram_channel">Telegram Channel</Label>
              <Input
                id="group_telegram_channel"
                placeholder="@channel or -1001234567890"
                value={telegramChannelId}
                onChange={(e) => setTelegramChannelId(e.target.value)}
                className="h-9"
              />
              <p className="text-xs text-muted-foreground">
                Enter the channel ID (e.g., -1001234567890). Leave empty to
                disable notifications.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="group_telegram_template">Message Template</Label>
              <Textarea
                id="group_telegram_template"
                placeholder="Enter message template..."
                value={telegramMessageTemplate}
                onChange={(e) => setTelegramMessageTemplate(e.target.value)}
                className="font-mono text-xs min-h-[200px]"
              />
              <p className="text-xs text-muted-foreground">
                Supports Markdown formatting. Use{" "}
                <code className="bg-secondary px-1 rounded">*bold*</code> for
                bold text.
              </p>

              <Collapsible
                open={isTemplateVarsOpen}
                onOpenChange={setIsTemplateVarsOpen}
              >
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-between h-auto p-2 hover:bg-secondary/50 text-xs"
                  >
                    <div className="flex items-center gap-2">
                      <Info className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        Available Template Variables
                      </span>
                    </div>
                    <ChevronDown
                      className={cn(
                        "w-3.5 h-3.5 text-muted-foreground transition-transform",
                        isTemplateVarsOpen && "transform rotate-180"
                      )}
                    />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pt-2">
                  <div className="border border-border rounded-md bg-card p-3 space-y-2">
                    <p className="text-xs text-muted-foreground">
                      Use these variables to customize your message format:
                    </p>
                    <div className="bg-secondary/30 border border-border rounded-md p-3 text-xs font-mono space-y-1.5">
                      <div className="flex items-start gap-2">
                        <code className="text-primary font-semibold">
                          {"{{title}}"}
                        </code>
                        <span className="text-muted-foreground">
                          - Job title
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <code className="text-primary font-semibold">
                          {"{{company}}"}
                        </code>
                        <span className="text-muted-foreground">
                          - Company name
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <code className="text-primary font-semibold">
                          {"{{type}}"}
                        </code>
                        <span className="text-muted-foreground">
                          - Job type (Hourly/OTE)
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <code className="text-primary font-semibold">
                          {"{{rate}}"}
                        </code>
                        <span className="text-muted-foreground">
                          - Compensation rate
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <code className="text-primary font-semibold">
                          {"{{location}}"}
                        </code>
                        <span className="text-muted-foreground">
                          - Job location
                        </span>
                      </div>
                      <div className="flex items-start gap-2">
                        <code className="text-primary font-semibold">
                          {"{{url}}"}
                        </code>
                        <span className="text-muted-foreground">
                          - Job URL (if available)
                        </span>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-border">
                      <p className="text-xs text-muted-foreground">
                        <strong className="text-foreground">
                          Conditional:
                        </strong>{" "}
                        Use{" "}
                        <code className="bg-secondary px-1 rounded">
                          {"{{#if url}}...{{/if}}"}
                        </code>{" "}
                        to conditionally include content when a URL is
                        available.
                      </p>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>

            <Button
              onClick={handleSaveTelegramChannel}
              disabled={isSavingTelegram}
              className="w-full sm:w-auto"
            >
              {isSavingTelegram ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Telegram Configuration"
              )}
            </Button>
          </div>
        </div>
      </TabsContent>

      <TabsContent
        value="runs"
        className="flex-1 flex flex-col min-h-0 mt-0 overflow-hidden"
      >
        {selectedRunId ? (
          // Run Detail View - shows logs and dataset
          <div className="flex flex-col h-full overflow-hidden">
            {/* Header with back button */}
            <div className="px-6 py-4 border-b border-border shrink-0">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToRuns}
                  className="h-8"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Runs
                </Button>
                <div>
                  <h3 className="text-sm font-semibold">Run Details</h3>
                  <p className="text-xs text-muted-foreground font-mono">
                    Run ID: {selectedRunId}
                  </p>
                </div>
              </div>
            </div>

            {/* Logs Section */}
            <div className="flex-1 flex flex-col min-h-0 px-6 py-4 border-b border-border">
              <div className="mb-3">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Logs
                </h4>
              </div>
              <div className="flex-1 min-h-0">
                <LogViewer logs={runLogs || []} />
              </div>
            </div>

            {/* Dataset Section */}
            <div className="flex-1 flex flex-col min-h-0 px-6 py-4 overflow-hidden">
              <div className="mb-3 flex items-center justify-between">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Dataset
                </h4>
                <div className="flex items-center gap-2">
                  {!isLoadingDataset && datasetItems.length > 0 && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={downloadAsJSON}
                        className="h-7 text-xs gap-1.5"
                      >
                        <Download className="w-3.5 h-3.5" />
                        JSON
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={downloadAsCSV}
                        className="h-7 text-xs gap-1.5"
                      >
                        <Download className="w-3.5 h-3.5" />
                        CSV
                      </Button>
                    </>
                  )}
                  {isLoadingDataset && (
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                  )}
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto">
                {isLoadingDataset ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : datasetItems.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground border border-border rounded-md">
                    <p className="text-sm">No items in this dataset</p>
                  </div>
                ) : (
                  <div className="border border-border rounded-md overflow-hidden">
                    <div className="bg-secondary/50 px-4 py-2 text-xs font-medium">
                      {datasetItems.length} items
                    </div>
                    <div className="max-h-full overflow-y-auto p-4 bg-black/5 dark:bg-black/20">
                      <pre className="text-xs font-mono">
                        {JSON.stringify(datasetItems.slice(0, 100), null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          // Runs List View
          <div className="flex-1 overflow-y-auto p-8">
            <div className="mb-6">
              <h3 className="text-sm font-semibold mb-1">Recent Runs</h3>
              <p className="text-xs text-muted-foreground">
                Click on a run to view its logs and dataset
              </p>
            </div>

            {!databaseRuns ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : databaseRuns.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground border border-border rounded-md">
                <p className="text-sm">No runs found for this group</p>
              </div>
            ) : (
              <ActorRunsTable
                runs={databaseRuns
                  .sort((a, b) => b.startedAt - a.startedAt)
                  .map((run) => ({
                    id: run.runId,
                    status: run.status,
                    startedAt: new Date(run.startedAt).toISOString(),
                    finishedAt: run.completedAt
                      ? new Date(run.completedAt).toISOString()
                      : undefined,
                    defaultDatasetId: undefined,
                  }))}
                selectedRunId={selectedRunId}
                onRunClick={handleRunClick}
              />
            )}
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
}
